import React, { useCallback, useMemo, useState } from 'react'
import { Box, Drawer, Grid, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material"
// icons
import SearchIcon from 'common/customIcons/search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// utils and helpers
import { debounce } from "utilities/common";
// custom components
import MDButton from "components/MDButton";
import { SubCategoryFilter, BoxFilter, BrandFilter } from "common/filters";
import InfiniteScrollTable from "components/tables/infiniteScrollTable";

export default function LowStockProducts({ open, TransitionComponent, handleClose, getDataMethod = () => { }, dataFilters = {} }) {
  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [refreshTable, setRefreshTable] = useState(false);

  delete customFilters[""];
  const columnDefs = [
    {
      headerName: 'Product Name/SKU',
      field: 'productName',
      suppressMenu: false,
      flex: 2,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { productName, sku, assets } = params?.data || {}
        const { thumbURL, name } = assets?.[0] || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          <img src={thumbURL} alt={name} width={30} height={30} />
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{productName}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Category',
      field: 'category',
      suppressMenu: false,
      flex: 1.5,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { category } = params?.data || {}

        return <Box>
          <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{category?.name}</Typography>
        </Box>
      }
    },
    {
      headerName: 'Brand',
      field: 'category',
      suppressMenu: false,
      flex: 1.5,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { brand } = params?.data || {}

        return <Box>
          <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{brand?.name}</Typography>
        </Box>
      }
    },
    {
      headerName: 'Quantity',
      field: 'quantity',
      suppressMenu: false,
      sortable: false,
      flex: 1,
    },
    {
      headerName: 'Price per Piece',
      field: 'pricePerPiece',
      suppressMenu: false,
      flex: 1,
      sortable: false,
      valueGetter: ({ data }) => data?.pricePerPiece ? `$${data?.pricePerPiece?.toLocaleString()}` : "-"

    },
    {
      headerName: 'Total Price',
      field: 'totalPrice',
      suppressMenu: false,
      sortable: false,
      flex: 1,
      valueGetter: ({ data }) => data?.totalPrice ? `$${data?.totalPrice?.toLocaleString()}` : "-"
    },
    {
      headerName: 'Promo',
      field: 'promo',
      suppressMenu: false,
      sortable: false,
      flex: 1,
      valueGetter: ({ data }) => data?.promo ? `${data?.promo}` : "-"

    },
    {
      headerName: 'Aging (Days)',
      field: 'agingDays',
      suppressMenu: false,
      sortable: false,
      flex: 1,
    },
    {
      headerName: 'Expiry (Days)',
      field: 'expiryDays',
      suppressMenu: false,
      sortable: false,
      flex: 1,
    },
  ];

  const handleDebounce = useCallback(debounce((event) => {
    const { name, value } = event.target;
    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleChange = async (e) => {
    const { name, value } = e.target
    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }

  const handleClearFilters = () => {
    setInputFilters({ search: customFilters.search || "" });
    setCustomFilters({ search: customFilters.search || "" });
    setRefreshTable(prev => !prev);
  }

  const isFilterSelected = useMemo(() => {
    // Return true if any filter in customFilters is selected (truthy)
    return (
      Object.values(customFilters || {}).some(Boolean)
    );
  }, [customFilters]);

  return (
    <Drawer
      open={open}
      TransitionComponent={TransitionComponent}
      keepMounted
      maxWidth='xs'
      fullWidth
      className="cus-drawer low-stock-products-drawer"
      onClose={handleClose}
      anchor='right'
    >
      <Box p={2} className='drawer-head-sec d-flex align-item-center jc-s-btwn'>
        <Typography variant='h4' fontWeight='semibold'>
          Low Stock Products
          <Tooltip arrow title={"Shows products which couldn't be distributed due to box limitation."}>
            <span style={{ position: "relative", top: "-5px", left: "1px" }}>
              <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
            </span>
          </Tooltip>
        </Typography>
        <IconButton onClick={handleClose} edge='end' size='small'>
          <CloseIcon fontSize='medium' />
        </IconButton>
      </Box>

      <Box p={{ xs: 2 }}>
        <Grid container alignItems="center" mb={2}>
          <Grid item xs={12} sm={4}></Grid>

          {/* Search Filter */}
          <Grid item xs={12} sm={8}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent={{ xs: 'start', sm: 'end' }}
            >
              <Box className="search-gray">
                <TextField
                  className="custom-search-field"
                  placeholder="Search"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">
                      <IconButton size='small'>
                        <SearchIcon width='18px' height='18px' />
                      </IconButton>
                    </InputAdornment>
                  }}
                  name='search'
                  value={inputFilters.search}
                  onChange={event => {
                    const { value } = event.target;
                    setInputFilters({ ...inputFilters, search: value })
                    handleDebounce(event)
                  }}
                  style={{ minWidth: "300px" }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec filter-gray-bg' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} lg={3} xxxl={2}>
              <BoxFilter
                value={customFilters?.boxId}
                handleChange={handleChange}
                placeholder={"Box Type"}
                selectAllLabel={"All Boxes"}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <SubCategoryFilter
                value={customFilters?.categoryId}
                handleChange={handleChange}
                placeholder={"Category"}
                selectAllLabel={"All Categories"}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <BrandFilter
                value={customFilters?.brandId}
                handleChange={handleChange}
                placeholder={"Brands"}
                selectAllLabel={"All"}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                onClick={handleClearFilters}
                disabled={!isFilterSelected}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        <div className="drawer-table-height" style={{ border: "1px solid #F1F1F1", borderRadius: "8px", overflow: "hidden" }}>
          {/* Products listing */}
          <InfiniteScrollTable
            tableName="Box Products"
            getDataMethod={getDataMethod}
            columnDefs={columnDefs}
            refreshTable={refreshTable}
            customFilters={{ ...customFilters, ...dataFilters }}
            tableHeight="77vh"
          />
        </div>
      </Box>
    </Drawer>
  )
}
